// Settings -> Integrations -> QuickBooks: connection health, the sync switch and the tenant's accounting
// mappings (BIL-CFG-TNT-04-001/002, BIL-FR-04-018). Shown once QuickBooks has been connected.
//
// What is configurable per tenant: whether sync runs at all, the QuickBooks service item (and, for a new
// item, its income account) invoice lines are booked to, which QuickBooks tax code each Smart Contractor
// tax code (TX-MAT / TX-SRV / TX-EXEMPT) maps to, and which QuickBooks payment method each Smart
// Contractor method maps to. The standard field mapping itself is platform-defined and shown read-only.
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from '@/components/UI/Alert';
import { Button } from '@/components/UI/Button';
import { Input } from '@/components/UI/Input';
import { Select } from '@/components/UI/Select';
import { Switch } from '@/components/UI/Switch';
import {
  useGetQuickBooksStatusQuery, useGetQuickBooksSyncOptionsQuery, useGetQuickBooksSyncSettingsQuery, useSaveQuickBooksSyncSettingsMutation,
} from '@/redux/internal-services/jobsApi';

const TAX_CODES = ['TX-MAT', 'TX-SRV', 'TX-EXEMPT'];
const PAYMENT_METHODS = ['ACH transfer', 'Check', 'Card', 'Cash', 'Other'];

const label = 'block text-micro font-bold uppercase tracking-[0.08em] text-ui-text3';

export default function QuickBooksSyncSettings({ onReconnect }: { onReconnect: () => void }) {
  const { t } = useTranslation();
  const { data: status } = useGetQuickBooksStatusQuery();
  const { data: cfg } = useGetQuickBooksSyncSettingsQuery();
  const { data: options } = useGetQuickBooksSyncOptionsQuery(undefined, { skip: !status || status.state !== 'connected' });
  const [save, { isLoading: saving }] = useSaveQuickBooksSyncSettingsMutation();

  const [enabled, setEnabled] = useState(true);
  const [item, setItem] = useState('');
  const [account, setAccount] = useState('');
  const [taxMap, setTaxMap] = useState<Record<string, string>>({});
  const [methodMap, setMethodMap] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    if (!cfg) return;
    setEnabled(cfg.sync_enabled !== false);
    setItem(cfg.service_item_name ?? '');
    setAccount(cfg.income_account_name ?? '');
    setTaxMap(cfg.tax_code_map ?? {});
    setMethodMap(cfg.payment_method_map ?? {});
  }, [cfg]);

  if (!status || status.state === 'not_connected') return null;
  const reconnect = status.state === 'reconnect_required';
  // Blank rows are not saved: an unmapped code simply falls back to the default behaviour.
  const clean = (m: Record<string, string>) => Object.fromEntries(Object.entries(m).filter(([, v]) => v.trim()).map(([k, v]) => [k, v.trim()]));

  const onSave = async () => {
    setMessage(null);
    try {
      await save({
        sync_enabled: enabled, service_item_name: item.trim(), income_account_name: account.trim(),
        tax_code_map: clean(taxMap), payment_method_map: clean(methodMap),
      }).unwrap();
      setMessage({ ok: true, text: t('settings.quickbooks.saved', 'QuickBooks sync settings saved.') });
    } catch (e) {
      const detail = (e as { data?: { detail?: string } })?.data?.detail;
      setMessage({ ok: false, text: detail ?? t('settings.quickbooks.saveFailed', 'Could not save the settings.') });
    }
  };

  return (
    <div className="card-surface mt-4 space-y-4 p-5">
      <div>
        <p className="text-sm font-semibold text-ui-text">{t('settings.quickbooks.title', 'QuickBooks sync')}</p>
        <p className="mt-0.5 text-xs text-ui-text3">
          {t('settings.quickbooks.intro', 'Approved invoices, their payments and cancellations sync to QuickBooks; payments recorded in QuickBooks come back here. Smart Contractor stays the record of truth — differences are flagged for reconciliation, never silently overwritten.')}
        </p>
      </div>

      {reconnect && (
        <Alert tone="warning">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span>
              {status.issue
                ? t('settings.quickbooks.reconnectIssue', 'QuickBooks must be reconnected ({{issue}}). Sync is paused and resumes by itself once you reconnect.', { issue: status.issue })
                : t('settings.quickbooks.reconnectNeeded', 'QuickBooks must be reconnected. Sync is paused and resumes by itself once you reconnect.')}
            </span>
            <Button size="sm" variant="primary" onClick={onReconnect}>{t('settings.quickbooks.reconnect', 'Reconnect')}</Button>
          </div>
        </Alert>
      )}

      <label className="flex items-center justify-between gap-3">
        <span>
          <span className="block text-sm font-medium text-ui-text">{t('settings.quickbooks.syncToggle', 'Sync invoices and payments')}</span>
          <span className="block text-xs text-ui-text3">{t('settings.quickbooks.syncToggleHint', 'Turn off to pause sync without disconnecting.')}</span>
        </span>
        <Switch checked={enabled} onCheckedChange={setEnabled} aria-label={t('settings.quickbooks.syncToggle', 'Sync invoices and payments')} />
      </label>

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="space-y-1.5">
          <span className={label}>{t('settings.quickbooks.serviceItem', 'QuickBooks service item')}</span>
          <Input value={item} onChange={(e) => setItem(e.target.value)} placeholder={t('settings.quickbooks.serviceItemPlaceholder', 'Roofing Services')} list="qb-items" />
          <datalist id="qb-items">{(options?.items ?? []).map((o) => <option key={o.id} value={o.name} />)}</datalist>
          <span className="block text-caption text-ui-text3">
            {t('settings.quickbooks.serviceItemHint', 'Invoice lines are booked to this item; it is created if it does not exist.')}
            {options?.items?.length ? ` ${t('settings.quickbooks.serviceItemsFound', '{{count}} service items found in QuickBooks — pick one or type a new name.', { count: options.items.length })}` : ''}
          </span>
        </label>
        <label className="space-y-1.5">
          <span className={label}>{t('settings.quickbooks.incomeAccount', 'Income account (new item)')}</span>
          <Input value={account} onChange={(e) => setAccount(e.target.value)} placeholder={t('settings.quickbooks.incomeAccountPlaceholder', 'First active income account')} list="qb-accounts" />
          <datalist id="qb-accounts">{(options?.income_accounts ?? []).map((o) => <option key={o.id} value={o.name} />)}</datalist>
          <span className="block text-caption text-ui-text3">{t('settings.quickbooks.incomeAccountHint', 'Only used when the item above has to be created.')}</span>
        </label>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <p className={label}>{t('settings.quickbooks.taxCodes', 'Tax codes → QuickBooks tax code id')}</p>
          {TAX_CODES.map((code) => (
            <div key={code} className="flex items-center gap-2">
              <span className="w-24 flex-shrink-0 text-xs text-ui-text2">{code}</span>
              {options?.tax_codes?.length ? (
                <Select value={taxMap[code] ?? ''} onChange={(e) => setTaxMap({ ...taxMap, [code]: e.target.value })} aria-label={t('settings.quickbooks.taxCodeFor', 'QuickBooks tax code for {{code}}', { code })}>
                  <option value="">{t('settings.quickbooks.notMapped', 'Not mapped')}</option>
                  {options.tax_codes.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </Select>
              ) : (
                <Input value={taxMap[code] ?? ''} onChange={(e) => setTaxMap({ ...taxMap, [code]: e.target.value })} placeholder={t('settings.quickbooks.notMapped', 'Not mapped')} aria-label={t('settings.quickbooks.taxCodeFor', 'QuickBooks tax code for {{code}}', { code })} />
              )}
            </div>
          ))}
          <p className="text-caption text-ui-text3">{t('settings.quickbooks.taxCodesHint', "Unmapped codes send only the invoice's tax total.")}</p>
        </div>
        <div className="space-y-2">
          <p className={label}>{t('settings.quickbooks.paymentMethods', 'Payment methods → QuickBooks method')}</p>
          {PAYMENT_METHODS.map((m) => (
            <div key={m} className="flex items-center gap-2">
              <span className="w-24 flex-shrink-0 text-xs text-ui-text2">{t(`billing.method.${m}`, m)}</span>
              {options?.payment_methods?.length ? (
                <Select value={methodMap[m] ?? ''} onChange={(e) => setMethodMap({ ...methodMap, [m]: e.target.value })} aria-label={t('settings.quickbooks.methodFor', 'QuickBooks payment method for {{method}}', { method: m })}>
                  <option value="">{t('settings.quickbooks.sameName', 'Same name')}</option>
                  {options.payment_methods.map((o) => <option key={o.id} value={o.name}>{o.name}</option>)}
                </Select>
              ) : (
                <Input value={methodMap[m] ?? ''} onChange={(e) => setMethodMap({ ...methodMap, [m]: e.target.value })} placeholder={t('settings.quickbooks.sameName', 'Same name')} aria-label={t('settings.quickbooks.methodFor', 'QuickBooks payment method for {{method}}', { method: m })} />
              )}
            </div>
          ))}
          <p className="text-caption text-ui-text3">{t('settings.quickbooks.methodsHint', 'Unmapped methods are matched by name.')}</p>
        </div>
      </div>

      {cfg?.platform?.field_mappings && (
        <details className="rounded-control border border-ui-border px-3 py-2 text-xs text-ui-text2">
          <summary className="cursor-pointer font-medium text-ui-text">{t('settings.quickbooks.fieldMapping', 'Standard field mapping (set by the platform)')}</summary>
          <div className="mt-2 grid gap-3 sm:grid-cols-2">
            {Object.entries(cfg.platform.field_mappings).map(([group, fields]) => (
              <div key={group}>
                <p className="font-semibold capitalize text-ui-text">{group}</p>
                <ul className="mt-0.5 space-y-0.5">
                  {Object.entries(fields).map(([k, v]) => <li key={k}><span className="text-ui-text3">{k}</span> → {v}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </details>
      )}

      {message && <Alert tone={message.ok ? 'success' : 'danger'}>{message.text}</Alert>}
      <div className="flex justify-end">
        <Button onClick={() => void onSave()} disabled={saving}>{saving ? t('settings.quickbooks.saving', 'Saving…') : t('settings.quickbooks.save', 'Save settings')}</Button>
      </div>
    </div>
  );
}
